import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import MultipleSelector from "@/components/ui/multiple-selector"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select"
import { GenericListManager } from "@/components/ui/generic-list-manager"
import { CurrencyInput } from "@/components/ui/currency-input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChevronDown, Plane, MapPin, Users, Calendar as CalendarIconLucide, Files, ArrowLeft, Loader2, Package, Utensils, Hotel, Car, Ship, ShieldCheck } from "lucide-react"
import { Calendar } from "@/components/ui/calendar"
import { countries, ecuadorProvinces } from "../utils/countrys-provinces"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { useAuth } from "@/context/AuthContext"
import { updateTravelRequest, type UpdateTravelRequestData } from "@/lib/collections/requestsTravels/user/updateRequestsTravel"
import { fetchUsersWithProfiles } from "@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile"
import { TravelRequestService, type TravelDocument } from "@/lib/collections/requestsTravels/user/getRequestTravelFetch"
import { cn } from "@/lib/utils"
import { fetchCompanies } from "@/lib/collections/organizations/getOrganizationsFetch"

type Collaborator = {
    label: string;
    value: string;
};

const EditTravelAdmin = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id: travelId } = useParams({ strict: false })

    const [title, setTitle] = useState("")
    const [description, setDescription] = useState("")
    const [openStartDate, setOpenStartDate] = useState(false)
    const [startDate, setStartDate] = useState<Date | undefined>(undefined)
    const [startTime, setStartTime] = useState("10:30")
    const [endDate, setEndDate] = useState<Date | undefined>(undefined)
    const [endTime, setEndTime] = useState("10:30")
    const [openEndDate, setOpenEndDate] = useState(false)
    const [isInternational, setIsInternational] = useState(false)
    const [address, setAddress] = useState("")
    const [collaborators, setCollaborators] = useState<Collaborator[]>([])
    const [availableCollaborators, setAvailableCollaborators] = useState<Collaborator[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedProvinces, setSelectedProvinces] = useState<{ label: string; value: string }[]>([])
    const [selectedCountries, setSelectedCountries] = useState<{ label: string; value: string }[]>([])
    const [transportationType, setTransportationType] = useState("")
    const [isGroup, setIsGroup] = useState(false)
    const [companies, setCompanies] = useState<{ label: string; value: string }[]>([])
    const [availableCompanies, setAvailableCompanies] = useState<{ label: string; value: string }[]>([])
    const [loadingCompanies, setLoadingCompanies] = useState(false)
    const [companiesLoadError, setCompaniesLoadError] = useState<string | null>(null)
    const [clients, setClients] = useState<string[]>([])
    const [costTransporte, setCostTransporte] = useState("")
    const [costHospedaje, setCostHospedaje] = useState("")
    const [costAlimentacion, setCostAlimentacion] = useState("")
    const [costVarios, setCostVarios] = useState("")
    const [usersLoadError, setUsersLoadError] = useState<string | null>(null)
    const [isLoadingTravel, setIsLoadingTravel] = useState(true)
    const [travelData, setTravelData] = useState<TravelDocument | null>(null)
    const [travelOwnerId, setTravelOwnerId] = useState<string>("")
    const [dateError, setDateError] = useState<string>("")

    const totalCost =
        (parseFloat(costTransporte) || 0) +
        (parseFloat(costHospedaje) || 0) +
        (parseFloat(costAlimentacion) || 0) +
        (parseFloat(costVarios) || 0)

    // ─ Cargar datos del viaje 
    useEffect(() => {
        const loadTravelData = async () => {
            if (!travelId) return
            try {
                setIsLoadingTravel(true)
                const travel = await TravelRequestService.getTravelById(travelId)
                setTravelData(travel)
                setTravelOwnerId(travel.userId)

                setTitle(travel.title)
                setDescription(travel.tripPurpose || "")
                setIsInternational(travel.isInternational)
                setIsGroup(travel.isGroup)
                setAddress(travel.address || "")
                setTransportationType(travel.transportationType || "")

                const costs = travel.estimatedCost || []
                setCostTransporte(costs[0]?.toString() || "")
                setCostHospedaje(costs[1]?.toString() || "")
                setCostAlimentacion(costs[2]?.toString() || "")
                setCostVarios(costs[3]?.toString() || "")
                setClients(travel.clients || [])

                const start = new Date(travel.startDate)
                const end = new Date(travel.endDate)
                setStartDate(start)
                setEndDate(end)
                setStartTime(`${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`)
                setEndTime(`${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`)

                if (travel.provinces && travel.provinces.length > 0) {
                    setSelectedProvinces(ecuadorProvinces.filter(p => travel.provinces?.includes(p.value)))
                }
                if (travel.countries && travel.countries.length > 0) {
                    setSelectedCountries(countries.filter(c => travel.countries?.includes(c.value)))
                }
            } catch {
                toast.error("Error al cargar los datos del viaje")
            } finally {
                setIsLoadingTravel(false)
            }
        }
        loadTravelData()
    }, [travelId])

    // ── Cargar colaboradores disponibles 
    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoadingUsers(true)
                setUsersLoadError(null)
                const usersWithProfiles = await fetchUsersWithProfiles()
                const options: Collaborator[] = usersWithProfiles
                    .filter(u => u.profile?.isActive !== false)
                    .map(u => ({ label: u.name, value: u.id }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                setAvailableCollaborators(options)
                if (options.length === 0) setUsersLoadError("No hay colaboradores disponibles")
            } catch (error: any) {
                setUsersLoadError(error.message || "Error al cargar colaboradores")
            } finally {
                setLoadingUsers(false)
            }
        }
        loadUsers()
    }, [])

    // ── Pre-seleccionar colaboradores ───────────────────────────────────────
    useEffect(() => {
        if (!travelData || availableCollaborators.length === 0) return
        if (travelData.usersId && travelData.usersId.length > 0) {
            const preSelected = availableCollaborators.filter(c =>
                travelData.usersId.includes(c.value) && c.value !== travelData.userId
            )
            setCollaborators(preSelected)
        }
    }, [travelData, availableCollaborators])

    // ── Cargar empresas ─────────────────────────────────────────────────────
    const loadCompanies = async () => {
        try {
            setLoadingCompanies(true)
            setCompaniesLoadError(null)
            const data = await fetchCompanies()
            const options = data
                .map(c => ({ label: c.name, value: c.$id }))
                .sort((a, b) => a.label.localeCompare(b.label))
            setAvailableCompanies(options)
        } catch (error: any) {
            setCompaniesLoadError(error.message || "Error al cargar empresas")
        } finally {
            setLoadingCompanies(false)
        }
    }

    useEffect(() => { loadCompanies() }, [])

    // ── Pre-seleccionar empresas ────────────────────────────────────────────
    useEffect(() => {
        if (!travelData || availableCompanies.length === 0) return
        const saved = travelData.companies ?? []
        if (saved.length > 0) {
            setCompanies(availableCompanies.filter(c => saved.includes(c.value)))
        }
    }, [travelData, availableCompanies])

    // Limpiar colaboradores si se deshabilita viaje grupal
    useEffect(() => {
        if (!isGroup) setCollaborators([])
    }, [isGroup])

    // ── Validar fechas ──────────────────────────────────────────────────────
    useEffect(() => {
        if (startDate && endDate && startTime && endTime) {
            const s = new Date(startDate)
            const [sh, sm] = startTime.split(":")
            s.setHours(parseInt(sh), parseInt(sm), 0, 0)
            const e = new Date(endDate)
            const [eh, em] = endTime.split(":")
            e.setHours(parseInt(eh), parseInt(em), 0, 0)
            setDateError(e <= s ? "La fecha final debe ser posterior a la fecha inicial" : "")
        }
    }, [startDate, endDate, startTime, endTime])

    // ── Reintentar carga de usuarios ────────────────────────────────────────
    const retryLoadUsers = async () => {
        setLoadingUsers(true)
        setUsersLoadError(null)
        try {
            const usersWithProfiles = await fetchUsersWithProfiles(true)
            const options: Collaborator[] = usersWithProfiles
                .filter(u => u.profile?.isActive !== false)
                .map(u => ({ label: u.name, value: u.id }))
                .sort((a, b) => a.label.localeCompare(b.label))
            setAvailableCollaborators(options)
            if (options.length === 0) setUsersLoadError("No hay colaboradores disponibles")
        } catch (error: any) {
            setUsersLoadError(error.message || "Error al cargar colaboradores")
        } finally {
            setLoadingUsers(false)
        }
    }

    // ── Guardar cambios ─────────────────────────────────────────────────────
    const handleSave = async () => {
        if (isSubmitting) return

        if (!title.trim()) { toast.warning("El título es obligatorio"); return }
        if (!startDate) { toast.warning("La fecha de inicio es obligatoria"); return }
        if (!endDate) { toast.warning("La fecha final es obligatoria"); return }
        if (!user?.$id) { toast.warning("Error: usuario no identificado"); return }
        if (dateError) { toast.warning(dateError); return }

        try {
            setIsSubmitting(true)
            const loadingToast = toast.loading("Actualizando solicitud de viaje...")

            const combinedStart = new Date(startDate)
            const [sh, sm] = startTime.split(":")
            combinedStart.setHours(parseInt(sh), parseInt(sm), 0, 0)

            const combinedEnd = new Date(endDate)
            const [eh, em] = endTime.split(":")
            combinedEnd.setHours(parseInt(eh), parseInt(em), 0, 0)

            const usersIdArray = [travelOwnerId || user.$id]
            if (isGroup && collaborators.length > 0) {
                usersIdArray.push(...collaborators.map(c => c.value))
            }

            const updateData: UpdateTravelRequestData = {
                userId: travelOwnerId || user.$id,
                title: title.trim(),
                tripPurpose: description.trim() || "Sin descripción",
                startDate: combinedStart.toISOString(),
                endDate: combinedEnd.toISOString(),
                status: travelData?.status ?? "pending",
                address: address.trim() || undefined,
                isInternational,
                transportationType: transportationType || undefined,
                estimatedCost: [
                    parseFloat(costTransporte) || 0,
                    parseFloat(costHospedaje) || 0,
                    parseFloat(costAlimentacion) || 0,
                    parseFloat(costVarios) || 0,
                ],
                companies: companies.map(c => c.value),
                clients,
                isGroup,
                usersId: usersIdArray,
                provinces: isInternational ? [] : selectedProvinces.map(p => p.value),
                countries: isInternational ? selectedCountries.map(c => c.value) : [],
            }

            await updateTravelRequest(
                travelId!,
                updateData,
                user.name,
                user.email,
                travelData?.status ?? "pending"
            )

            toast.dismiss(loadingToast)
            toast.success("Solicitud de viaje actualizada exitosamente")
            navigate({ to: "/requests/viewTravel/listRequestRrhh" })
        } catch {
            toast.error("Error al actualizar la solicitud de viaje")
        } finally {
            setIsSubmitting(false)
        }
    }

    // ── Loading inicial ─────────────────────────────────────────────────────
    if (isLoadingTravel) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <p className="text-slate-600 dark:text-slate-400">Cargando datos del viaje...</p>
                </div>
            </div>
        )
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Permisos</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menú</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests/viewTravel/listRequestRrhh">
                                Gestión de viajes
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Editar Viaje (Admin)</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            <Plane className="w-5 h-5" />
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 font-outfit">
                <div className="max-w-8xl mx-auto">
                    <Card className="shadow-xl border-0 p-0">
                        <CardHeader className="relative text-white rounded-t-lg bg-violet-600 h-20">
                            <div className="absolute top-0 rounded-t-4xl left-0 w-full h-full bg-violet-600"></div>
                            <div className="relative z-10 flex items-center gap-4 py-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <ShieldCheck className="w-6 h-6" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <CardTitle className="text-2xl">Editar Solicitud (Admin)</CardTitle>
                                    <CardDescription className="text--100">
                                        {travelData?.title ?? "Cargando..."}
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>

                        <CardContent className="p-6 sm:p-8">
                            <div className="space-y-8">

                                {/* ── 1. Información Básica ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">1</div>
                                        Información Básica
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="title" className="text-sm font-medium">Título *</Label>
                                            <Input id="title" value={title} onChange={e => setTitle(e.target.value)}
                                                placeholder="Ej: Reunión con cliente en Guayaquil" className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label htmlFor="description" className="text-sm font-medium">Motivo detallado</Label>
                                            <Textarea id="description" value={description} onChange={e => setDescription(e.target.value)}
                                                placeholder="Describe el motivo del viaje, objetivos y actividades planeadas..."
                                                className="mt-1.5 min-h-25" />
                                        </div>
                                    </div>
                                </section>

                                {/* ── 2. Fechas y Horarios ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">2</div>
                                        Fechas y Horarios
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Inicio */}
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                                    <CalendarIconLucide className="w-4 h-4" /> Fecha inicial *
                                                </Label>
                                                <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                                                    <PopoverTrigger asChild>
                                                        <Button variant="outline" className={cn("w-full justify-between font-normal", dateError && "border-red-500")}>
                                                            {startDate
                                                                ? startDate.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
                                                                : "Seleccionar fecha"}
                                                            <ChevronDown className="w-4 h-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar mode="single" selected={startDate} captionLayout="dropdown"
                                                            onSelect={date => { setStartDate(date); setOpenStartDate(false) }} />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div>
                                                <Label htmlFor="start-time" className="text-sm font-medium">Hora inicial</Label>
                                                <Input type="time" id="start-time" value={startTime} onChange={e => setStartTime(e.target.value)}
                                                    className={cn("mt-1.5 [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer", dateError && "border-red-500")} />
                                            </div>
                                        </div>
                                        {/* Fin */}
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                                    <CalendarIconLucide className="w-4 h-4" /> Fecha final *
                                                </Label>
                                                <Popover open={openEndDate} onOpenChange={setOpenEndDate}>
                                                    <PopoverTrigger asChild>
                                                        <Button variant="outline" className={cn("w-full justify-between font-normal", dateError && "border-red-500")}>
                                                            {endDate
                                                                ? endDate.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
                                                                : "Seleccionar fecha"}
                                                            <ChevronDown className="w-4 h-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar mode="single" selected={endDate} captionLayout="dropdown"
                                                            onSelect={date => { setEndDate(date); setOpenEndDate(false) }} />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div>
                                                <Label htmlFor="end-time" className="text-sm font-medium">Hora final</Label>
                                                <Input type="time" id="end-time" value={endTime} onChange={e => setEndTime(e.target.value)}
                                                    className={cn("mt-1.5 [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer", dateError && "border-red-500")} />
                                            </div>
                                        </div>
                                        {dateError && (
                                            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2 col-span-full">
                                                <span>⚠</span> {dateError}
                                            </p>
                                        )}
                                    </div>
                                </section>

                                {/* ── 3. Destino y Transporte ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">3</div>
                                        Destino y Transporte
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <Switch id="isInternational" checked={isInternational} onCheckedChange={setIsInternational} />
                                            <Label htmlFor="isInternational" className="cursor-pointer flex items-center gap-2">
                                                <MapPin className="w-4 h-4" /> ¿Es un viaje internacional?
                                            </Label>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-sm font-medium">
                                                    {isInternational ? "País(es)" : "Provincia(s)"}
                                                </Label>
                                                {isInternational ? (
                                                    <div className="mt-1.5">
                                                        <MultipleSelector key="countries" value={selectedCountries} onChange={setSelectedCountries}
                                                            defaultOptions={countries} placeholder="Seleccionar países..."
                                                            emptyIndicator={<p className="text-center text-sm py-4 text-slate-500">Sin resultados</p>} />
                                                    </div>
                                                ) : (
                                                    <div className="mt-1.5">
                                                        <MultipleSelector key="provinces" value={selectedProvinces} onChange={setSelectedProvinces}
                                                            defaultOptions={ecuadorProvinces} placeholder="Seleccionar provincias..."
                                                            emptyIndicator={<p className="text-center text-sm py-4 text-slate-500">Sin resultados</p>} />
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <Label htmlFor="transport" className="text-sm font-medium">Tipo de transporte</Label>
                                                <Select value={transportationType} onValueChange={setTransportationType}>
                                                    <SelectTrigger className="mt-1.5">
                                                        <SelectValue placeholder="Seleccionar tipo" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectGroup>
                                                            <SelectLabel>Tipo de transporte</SelectLabel>
                                                            <SelectItem value="land"><Car /> Terrestre</SelectItem>
                                                            <SelectItem value="air"><Plane /> Aéreo</SelectItem>
                                                            <SelectItem value="sea"><Ship /> Marítimo</SelectItem>
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="address" className="text-sm font-medium">Dirección detallada</Label>
                                            <Input id="address" value={address} onChange={e => setAddress(e.target.value)}
                                                placeholder="Dirección completa del destino" className="mt-1.5" />
                                        </div>
                                    </div>
                                </section>

                                {/* ── 4. Colaboradores ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">4</div>
                                        Colaboradores
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <Switch id="isGroup" checked={isGroup} onCheckedChange={setIsGroup} />
                                            <Label htmlFor="isGroup" className="cursor-pointer flex items-center gap-2">
                                                <Users className="w-4 h-4" /> ¿Viaje grupal con más colaboradores?
                                            </Label>
                                        </div>
                                        {isGroup && (
                                            <div className="space-y-3">
                                                {usersLoadError && (
                                                    <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                                                No se pudieron cargar los colaboradores
                                                            </p>
                                                            <Button onClick={retryLoadUsers} disabled={loadingUsers} size="sm" variant="outline"
                                                                className="mt-2 h-8 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300">
                                                                {loadingUsers ? "Cargando..." : "Reintentar"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                <Label className="text-sm font-medium">Selecciona colaboradores</Label>
                                                {collaborators.length > 0 && (
                                                    <p className="text-sm text-violet-600 dark:text-violet-400">
                                                        Seleccionados: <span className="text-slate-600 dark:text-slate-400">
                                                            {collaborators.map(c => c.label).join(", ")}
                                                        </span>
                                                    </p>
                                                )}
                                                <MultipleSelector value={collaborators} onChange={setCollaborators}
                                                    defaultOptions={availableCollaborators}
                                                    placeholder={loadingUsers ? "Cargando colaboradores..." : "Buscar colaboradores..."}
                                                    disabled={loadingUsers || !!usersLoadError}
                                                    emptyIndicator={
                                                        <p className="text-center text-sm py-4 text-slate-500">
                                                            {loadingUsers ? "Cargando..." : usersLoadError ? "Error al cargar." : "Sin colaboradores"}
                                                        </p>
                                                    } />
                                                {!loadingUsers && !usersLoadError && availableCollaborators.length > 0 && (
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {availableCollaborators.length} colaborador{availableCollaborators.length !== 1 ? "es" : ""} disponible{availableCollaborators.length !== 1 ? "s" : ""}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/* ── 5. Información Financiera ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">5</div>
                                        Información Financiera
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-sm font-medium"><Car /> Transporte (USD)</Label>
                                            <CurrencyInput value={costTransporte} onChange={setCostTransporte} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Hotel /> Hospedaje (USD)</Label>
                                            <CurrencyInput value={costHospedaje} onChange={setCostHospedaje} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Utensils /> Alimentación (USD)</Label>
                                            <CurrencyInput value={costAlimentacion} onChange={setCostAlimentacion} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Package /> Varios (USD)</Label>
                                            <CurrencyInput value={costVarios} onChange={setCostVarios} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                    </div>
                                    {totalCost > 0 && (
                                        <div className="mt-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                                            <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                                                Total estimado: ${totalCost.toFixed(2)} USD
                                            </p>
                                        </div>
                                    )}
                                </section>

                                {/* ── 6. Entidades y Clientes ── */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 dark:text-violet-300 text-sm font-bold">6</div>
                                        Entidades y Clientes
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <Label className="text-sm font-medium">Empresas o instituciones a visitar</Label>
                                            {companiesLoadError && (
                                                <div className="flex items-start gap-3 p-3 mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">No se pudieron cargar las empresas</p>
                                                        <Button onClick={loadCompanies} disabled={loadingCompanies} size="sm" variant="outline"
                                                            className="mt-2 h-8 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300">
                                                            {loadingCompanies ? "Cargando..." : "Reintentar"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="mt-1.5">
                                                <MultipleSelector value={companies} onChange={setCompanies}
                                                    defaultOptions={availableCompanies}
                                                    placeholder={loadingCompanies ? "Cargando empresas..." : "Buscar empresas..."}
                                                    disabled={loadingCompanies || !!companiesLoadError}
                                                    badgeClassName="max-w-[180px] truncate"
                                                    emptyIndicator={
                                                        <p className="text-center text-sm py-4 text-slate-500">
                                                            {loadingCompanies ? "Cargando..." : companiesLoadError ? "Error al cargar." : "Sin empresas"}
                                                        </p>
                                                    } />
                                            </div>
                                            {!loadingCompanies && !companiesLoadError && availableCompanies.length > 0 && (
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {availableCompanies.length} empresa{availableCompanies.length !== 1 ? "s" : ""} disponible{availableCompanies.length !== 1 ? "s" : ""}
                                                </p>
                                            )}
                                        </div>
                                        <GenericListManager items={clients} onChange={setClients}
                                            label="Clientes" placeholder="Nombre del cliente..."
                                            singularLabel="cliente" pluralLabel="clientes" />
                                    </div>
                                </section>
                            </div> 


                            {/* ── Botones ── */}
                            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t">
                                <Button onClick={handleSave} disabled={isSubmitting}
                                    className="flex-1 bg-violet-600 hover:bg-violet-700 cursor-pointer text-white font-medium">
                                    {isSubmitting ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
                                    ) : "Guardar Cambios"}
                                </Button>
                                <Button variant="outline" className="flex-1 sm:flex-none sm:px-8 cursor-pointer"
                                    disabled={isSubmitting}
                                    onClick={() => navigate({ to: "/requests/viewTravel/listRequestRrhh" })}>
                                    <ArrowLeft className="mr-2 h-4 w-4" /> Cancelar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    )
}

export default EditTravelAdmin
